import { useEffect, useState, type InputEvent, type SubmitEvent } from 'react'
import {
  confirmPasswordReset,
  signInWithEmailAndPassword,
  verifyPasswordResetCode,
} from 'firebase/auth'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { authErrorMessage } from '../lib/authErrors'
import { rememberLastUsedMethod } from '../lib/lastUsedMethod'
import AuthCard from '../components/auth/AuthCard'
import AuthField from '../components/auth/AuthField'
import { auth } from '../lib/firebase'
import { collectErrors, type FieldErrors } from '../lib/formErrors'

const MESSAGES = { password: 'Password must be at least 6 characters.' }

export default function ResetPassword() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const code = params.get('oobCode') ?? ''
  const [email, setEmail] = useState('')
  const [checking, setChecking] = useState(true)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    verifyPasswordResetCode(auth, code)
      .then(setEmail)
      .catch((err: unknown) => setError(authErrorMessage(err)))
      .finally(() => setChecking(false))
  }, [code])

  function clearFieldError(event: InputEvent<HTMLFormElement>) {
    const { name } = event.target as HTMLInputElement
    setFieldErrors((current) => ({ ...current, [name]: '' }))
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const invalid = collectErrors(form, MESSAGES)
    setFieldErrors(invalid)
    if (Object.keys(invalid).length > 0) return

    const password = String(new FormData(form).get('password'))
    setError('')
    setSubmitting(true)

    try {
      await confirmPasswordReset(auth, code, password)
      await signInWithEmailAndPassword(auth, email, password)
      rememberLastUsedMethod('email')
      navigate('/', { replace: true })
    } catch (err) {
      setError(authErrorMessage(err))
      setSubmitting(false)
    }
  }

  if (checking) return <AuthCard title="Checking your link" subtitle="One moment." />

  if (!email) {
    return (
      <AuthCard
        title="This link doesn't work"
        subtitle={error || 'Reset links expire after a while and can only be used once.'}
        footer={
          <Link to="/forgot-password" className="font-medium text-purple-700 hover:text-purple-800">
            Request a new link
          </Link>
        }
      />
    )
  }

  return (
    <AuthCard
      title="Set a new password"
      subtitle={`You're resetting the password for ${email}.`}
      footer={
        <Link to="/login" className="font-medium text-purple-700 hover:text-purple-800">
          ← Back to log in
        </Link>
      }
    >
      <form noValidate onSubmit={handleSubmit} onInput={clearFieldError} className="space-y-4">
        <AuthField
          label="New password"
          name="password"
          type="password"
          autoComplete="new-password"
          placeholder="At least 6 characters"
          minLength={6}
          error={fieldErrors.password}
          required
        />

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="h-11 w-full rounded-[10px] bg-linear-to-b from-stone-700 to-stone-950 text-[15px] font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] transition-opacity enabled:hover:opacity-90 disabled:opacity-60"
        >
          {submitting ? 'Saving…' : 'Set new password'}
        </button>
      </form>
    </AuthCard>
  )
}
